import * as React from 'react';

import { isBefore, parseISO, subMonths } from 'date-fns';
import { FullMatchFragment } from '../../../generated/graphql';
import { Card } from '../../../components/Card';
import { hasScore } from '../matchUtils';
import { paths } from '../../../paths';
import { CardHeading } from '../../../components/CardHeading';
import { useMatchStats } from '../Stats/useMatchStats';
import { isNil } from '../../../utils/isNil';
import { Button } from '../../../components/Button';
import { TodoItem } from '../../../components/TodoItem';
import { useDismissMatchPrompts } from '../components/useDismissMatchPrompts';
import { Transition } from '../../../components/Transition';
import { useNow } from '../../../hooks/useNow';

interface Props {
  match: FullMatchFragment;
}

export const PostMatchPrompts: React.FC<Props> = ({ match }) => {
  const [hasDismissed, dismissPrompt] = useDismissMatchPrompts(
    match.id,
    'post',
  );

  const {
    fetching: statsLoading,
    stats,
    error,
  } = useMatchStats(match.id, {
    pause: hasDismissed,
  });
  const now = useNow();

  const show = !(
    statsLoading ||
    hasDismissed ||
    // don't show the prompts for really old matches
    isBefore(parseISO(match.playedAt), subMonths(now, 1))
  );

  const completions = {
    team: match.players.length > 0,
    score: hasScore(match),
    scorers: !isNil(stats.scorers),
    assists: !isNil(stats.assists),
    cards: !isNil(stats.cards),
    matchReport: !isNil(match.matchReport),
  };

  return (
    <Transition show={show}>
      <Card className="space-y-6">
        <CardHeading>Add Match Info</CardHeading>
        <ul className="space-y-3">
          {match.team.isCaptain ? (
            <TodoItem
              complete={completions.team}
              linkTo={paths.match.settings.squad(match.id)}
            >
              Set team
            </TodoItem>
          ) : null}

          <TodoItem
            complete={completions.score}
            linkTo={paths.match.settings.score(match.id)}
          >
            Score
          </TodoItem>
          {isNil(error) ? (
            <>
              <TodoItem
                complete={completions.scorers}
                linkTo={paths.match.settings.scorers(match.id)}
              >
                Scorers
              </TodoItem>
              <TodoItem
                complete={completions.assists}
                linkTo={paths.match.settings.assists(match.id)}
              >
                Assists
              </TodoItem>
              <TodoItem
                complete={completions.cards}
                linkTo={paths.match.settings.cards(match.id)}
              >
                Cards
              </TodoItem>
            </>
          ) : null}
          <TodoItem
            complete={completions.matchReport}
            linkTo={paths.match.settings.matchReport(match.id)}
          >
            Match report
          </TodoItem>
        </ul>

        <Button type="button" kind="plain" onClick={dismissPrompt}>
          Done
        </Button>
      </Card>
    </Transition>
  );
};
